import { z } from "zod";
/* Staff-authored guidance on a customer — the clinic console's Plan strip.
 *
 * An action is guidance, not an entry in the official patient record (that
 * lives in the EMR) and not a prescription. The software never proposes its
 * content: every field below is something a named staff member typed. Author
 * name and title are snapshotted from the signed-in identity at write time
 * and are deliberately absent from the create requests.
 *
 * Append-only on the wire as well as in the table (052_patient_actions.sql):
 * there is no update or delete shape. A change is a successor that points at
 * the action it replaced; a check-in ends or continues an action without
 * rewriting it.
 */
export const actionKindSchema = z.enum(["supplement", "diet", "lab", "visit"]);
export const checkInChannelSchema = z.enum(["chat", "video_visit", "lab_retest"]);
export const checkInOutcomeSchema = z.enum(["continue", "stop", "complete"]);
export const staffTitleSchema = z.enum(["physician", "nutrition_coach"]);
export const ACTION_TITLE_MAX = 120;
export const ACTION_INSTRUCTIONS_MAX = 2000;
export const ACTION_CHANGE_REASON_MAX = 500;
export const ACTION_CHECK_IN_NOTE_MAX = 2000;
export const ACTION_MAX_WEEKS = 104;
const isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
export const patientActionSchema = z.object({
    id: z.string(),
    kind: actionKindSchema,
    title: z.string(),
    instructions: z.string().nullable(),
    runsForWeeks: z.number().int().nullable(),
    checkInOn: isoDateSchema.nullable(),
    checkInChannel: checkInChannelSchema.nullable(),
    clientVisible: z.boolean(),
    sourceMessageId: z.string().nullable(),
    replacesId: z.string().nullable(),
    changeReason: z.string().nullable(),
    authorId: z.string(),
    authorName: z.string(),
    authorTitle: staffTitleSchema,
    createdAt: z.string(),
});
export const patientActionCheckInSchema = z.object({
    id: z.string(),
    actionId: z.string(),
    outcome: checkInOutcomeSchema,
    note: z.string().nullable(),
    nextCheckInOn: isoDateSchema.nullable(),
    nextCheckInChannel: checkInChannelSchema.nullable(),
    authorId: z.string(),
    authorName: z.string(),
    authorTitle: staffTitleSchema,
    createdAt: z.string(),
});
export const patientActionsResponseSchema = z.object({
    actions: z.array(patientActionSchema),
    checkIns: z.array(patientActionCheckInSchema),
});
export const patientActionCreateSchema = z
    .object({
    kind: actionKindSchema,
    title: z.string().trim().min(1).max(ACTION_TITLE_MAX),
    instructions: z.string().trim().min(1).max(ACTION_INSTRUCTIONS_MAX).optional(),
    runsForWeeks: z.number().int().min(1).max(ACTION_MAX_WEEKS).nullable(),
    checkInOn: isoDateSchema.optional(),
    checkInChannel: checkInChannelSchema.optional(),
    clientVisible: z.boolean(),
    sourceMessageId: z.string().uuid().optional(),
    replacesId: z.string().uuid().optional(),
    changeReason: z.string().trim().min(1).max(ACTION_CHANGE_REASON_MAX).optional(),
})
    .superRefine((req, ctx) => {
    if ((req.checkInOn === undefined) !== (req.checkInChannel === undefined)) {
        ctx.addIssue({
            code: "custom",
            path: ["checkInChannel"],
            message: "checkInOn and checkInChannel go together",
        });
    }
    if (req.changeReason !== undefined && req.replacesId === undefined) {
        ctx.addIssue({
            code: "custom",
            path: ["changeReason"],
            message: "changeReason needs replacesId",
        });
    }
});
export const patientActionCheckInCreateSchema = z
    .object({
    outcome: checkInOutcomeSchema,
    note: z.string().trim().min(1).max(ACTION_CHECK_IN_NOTE_MAX).optional(),
    nextCheckInOn: isoDateSchema.optional(),
    nextCheckInChannel: checkInChannelSchema.optional(),
})
    .superRefine((req, ctx) => {
    const isContinue = req.outcome === "continue";
    if (isContinue !== (req.nextCheckInOn !== undefined)) {
        ctx.addIssue({
            code: "custom",
            path: ["nextCheckInOn"],
            message: "nextCheckInOn is required when the outcome is continue, and only then",
        });
    }
    if ((req.nextCheckInOn === undefined) !== (req.nextCheckInChannel === undefined)) {
        ctx.addIssue({
            code: "custom",
            path: ["nextCheckInChannel"],
            message: "nextCheckInOn and nextCheckInChannel go together",
        });
    }
});
export const staffProfileSchema = z.object({
    name: z.string().nullable(),
    title: staffTitleSchema.nullable(),
});
